import {find as _find} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {NoResultsHeader} from '~/GalleryPB/NoResultsHeader';
import {Link} from '~library/atoms/Link';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {mediumAndUpQuery} from '~libSass/base/_exports.sass';

const TemplateRow = React.lazy(() =>
  import('~/GalleryPB/TemplateRow').then((module) => ({default: module.TemplateRow}))
);

function* generateRows(rowSize, premiums, frees, galleryPromoTile) {
  let f = 0;
  let p = 0;
  let premiumRow = true;
  let done = false;
  let row = [];
  let promoTileInserted = false;
  while (!done) {
    /* Insert Promo Tile in the 7 and 8 spot of the results */
    if (galleryPromoTile?.src && f + p === 6 && !promoTileInserted) {
      row.push({
        ...galleryPromoTile,
        isPromoTile: true,
      });
      row.push({});
      promoTileInserted = true;
    } else if (p < premiums.length && premiumRow) {
      row.push({...premiums[p], pos: p});
      p++;
    } else if (f < frees.length) {
      row.push({...frees[f], pos: f});
      f++;
    } else if (p < premiums.length) {
      row.push({...premiums[p], pos: p});
      p++;
    } else done = true;
    if (row.length === rowSize || (done && row.length > 0)) {
      yield row;
      row = [];
      premiumRow = !premiumRow;
    }
  }
}

export const GalleryResults = ({
  premiumTemplates,
  freeTemplates,
  showNoResultsHeader = false,
  favorites,
  categoryId,
  onTemplateClick,
  templatesPerRowOnDesktop = 4,
}) => {
  const {matches: mediumScreens} = useMatchQuery(`(${mediumAndUpQuery})`);
  const galleryPromoTile = window.gallery_promo_tile; // TODO: don't use the window for this data
  let {gallery_promo_row: promos = []} = window;

  if (!Array.isArray(promos)) {
    // Wrap legacy gallery_promo_rows object in an array Can remove once
    // EVT-30318 is released.
    promos = [{...promos, order: 1}];
  }

  if (!mediumScreens) {
    // EVT-30138 - doubles the order for mobile devices since original ordering
    // was after first row for desktop and after the second row for mobile
    promos = promos.map(({order, ...promo}) => ({...promo, order: order * 2}));
  }

  const templateRows = [
    ...generateRows(
      mediumScreens ? templatesPerRowOnDesktop : 2,
      premiumTemplates,
      freeTemplates,
      galleryPromoTile
    ),
  ];

  function renderTemplateRow(row, index) {
    return (
      <React.Suspense fallback={<div />} key={`galleryRow_${index + 1}`}>
        <TemplateRow
          results={row}
          type="free"
          favorites={favorites}
          screen={mediumScreens ? 'desktop' : 'mw'}
          categoryId={categoryId}
          onTemplateClick={onTemplateClick}
          templatesPerRowDesktop={templatesPerRowOnDesktop}
          loading={index < 2 ? 'eager' : 'lazy'}
        />
      </React.Suspense>
    );
  }

  const bottomAd = _find(promos, ({order}) => order < 0);

  return (
    <div className="gallery__results-wrapper" data-qa-id="galleryResultsGrid">
      {showNoResultsHeader && <NoResultsHeader />}
      {templateRows.map((row, index) => {
        const {order, ...promo} = _find(promos, {order: index}) || {};

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: existing error
          <React.Fragment key={index}>
            {order === index && <GalleryPromo promo={promo} />}
            {renderTemplateRow(row, index)}
          </React.Fragment>
        );
      })}
      {bottomAd && <GalleryPromo promo={bottomAd} />}
    </div>
  );
};

const GalleryPromo = ({promo}) => {
  const {matches: mediumScreens} = useMatchQuery(`(${mediumAndUpQuery})`);

  return (
    !!Object.keys(promo).length && (
      <React.Fragment key={promo.src + promo.order}>
        <Link
          href={promo.href}
          data-qa-id={promo['data-qa-id'] || 'gallery-promo-row'}
          className="gallery-promo-row"
          key="galleryPromoRow"
        >
          <img
            className="gallery-promo-row__image"
            src={mediumScreens ? promo.src : promo.mobile_src}
            alt={promo.alt}
            width="1"
            height="1"
          />
        </Link>
      </React.Fragment>
    )
  );
};

GalleryResults.propTypes = {
  freeTemplates: PropTypes.arrayOf(
    PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
        PropTypes.number,
        PropTypes.bool,
      ])
    )
  ).isRequired,
  premiumTemplates: PropTypes.arrayOf(
    PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
        PropTypes.number,
        PropTypes.bool,
      ])
    )
  ).isRequired,
  favorites: PropTypes.arrayOf(PropTypes.string).isRequired,
  showNoResultsHeader: PropTypes.bool,
  categoryId: PropTypes.string,
  onTemplateClick: PropTypes.func,
  templatesPerRowOnDesktop: PropTypes.number,
};
